import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus(null);
    setFormError(null);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt' && typeof value === 'string') {
          body.append(key, value);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9oo5qiftmtnfcfh0aqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError(serverMsg);
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <>
      <title>关于我们 - 非常爱漫画 | 爱漫画品牌介绍与联系方式</title>
      <meta name="description" content="了解爱漫画品牌故事，非常爱漫画专注免费漫画在线阅读，提供海量国漫日漫韩漫资源。查看平台口碑评价与联系方式。" />
      <meta name="keywords" content="爱漫画, 非常爱漫画, 爱漫画品牌, 漫画平台, 关于爱漫画, 漫画网站介绍, 免费漫画平台" />
      <link rel="canonical" href="https://www.aimanhua.com/about" />
      <meta property="og:title" content="关于我们 - 非常爱漫画 | 爱漫画品牌介绍与联系方式" />
      <meta property="og:description" content="了解爱漫画品牌故事，非常爱漫画专注免费漫画在线阅读，提供海量国漫日漫韩漫资源。查看平台口碑评价与联系方式。" />
      <meta property="og:url" content="https://www.aimanhua.com/about" />
      <meta property="og:type" content="website" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "关于我们 - 非常爱漫画",
          "description": "了解爱漫画品牌故事，非常爱漫画专注免费漫画在线阅读，提供海量国漫日漫韩漫资源。查看平台口碑评价与联系方式。",
          "url": "https://www.aimanhua.com/about",
          "isPartOf": {
            "@type": "WebSite",
            "name": "爱漫画",
            "url": "https://www.aimanhua.com"
          }
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.aimanhua.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "关于我们",
              "item": "https://www.aimanhua.com/about"
            }
          ]
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "非常爱漫画真的完全免费吗？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "是的，非常爱漫画平台上所有漫画资源均免费提供阅读。我们致力于为漫迷打造无障碍的阅读体验，无需付费即可畅读全部内容。"
              }
            },
            {
              "@type": "Question",
              "name": "漫画更新频率如何？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "我们每天都会同步各大漫画平台的最新连载内容，确保您能第一时间追更喜欢的漫画。大部分热门漫画的更新延迟不超过24小时。"
              }
            },
            {
              "@type": "Question",
              "name": "支持哪些设备阅读？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "非常爱漫画完美适配电脑、平板和手机端，支持下拉式阅读模式。无论您使用什么设备，都能获得流畅舒适的阅读体验。"
              }
            },
            {
              "@type": "Question",
              "name": "如何找到我想看的漫画？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "您可以通过首页的分类导航按类型浏览，也可以使用搜索功能直接查找漫画名称。我们还设有热门推荐和最近更新栏目，帮助您发现更多精彩内容。"
              }
            },
            {
              "@type": "Question",
              "name": "漫画画质如何？",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "非常爱漫画对漫画画质有严格标准，大部分漫画提供高清分辨率阅读，确保文字清晰、画面细腻，给您最佳的视觉享受。"
              }
            }
          ]
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span>/</span>
                <span className="text-foreground-800 font-medium">关于我们</span>
              </nav>
            </div>
          </div>

          <section className="relative py-16 md:py-20 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20artistic%20illustration%20of%20bookshelves%20filled%20with%20colorful%20manga%20and%20comic%20books%20in%20a%20cozy%20library%20setting%2C%20soft%20ambient%20lighting%20streaming%20through%20windows%2C%20comfortable%20reading%20atmosphere%2C%20warm%20amber%20and%20cream%20tones%2C%20inviting%20and%20peaceful%20composition&width=1920&height=500&seq=about-hero-bg&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60"></div>
            <div className="relative z-10 max-w-screen-2xl mx-auto px-4 md:px-6 text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
                关于 <span className="text-primary-400">非常爱漫画</span>
              </h1>
              <p className="text-sm md:text-base text-white/70 max-w-xl mx-auto">
                专注免费漫画在线阅读，致力于为每一位漫迷提供最优质的阅读体验
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6 text-center">
                  品牌故事
                </h2>
                <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    非常爱漫画创立于2018年，源于创始团队对漫画文化的无限热爱。我们发现很多优秀的漫画作品因为平台门槛、付费墙等原因无法被更多读者看到，于是立志打造一个真正免费、开放、高质量的漫画阅读平台。
                  </p>
                  <p>
                    经过多年的发展，非常爱漫画已经积累了海量的国漫、日漫、韩漫资源，覆盖热血战斗、甜蜜恋爱、玄幻修仙、轻松搞笑、悬疑推理、都市情感、科幻未来等全品类。平台支持下拉式阅读，无论是电脑还是手机都能流畅体验。
                  </p>
                  <p>
                    我们的愿景是让每一位漫迷都能在非常爱漫画找到属于自己的精神世界。无论你是喜欢燃到爆炸的热血少年漫，还是偏爱细腻动人的恋爱故事，这里都有你想要的精彩。
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 max-w-4xl mx-auto">
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">2018</div>
                  <p className="text-sm text-foreground-600">创始年份</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600 mb-2">10万+</div>
                  <p className="text-sm text-foreground-600">漫画资源</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">500万+</div>
                  <p className="text-sm text-foreground-600">活跃读者</p>
                </div>
                <div className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-accent-600 mb-2">99%</div>
                  <p className="text-sm text-foreground-600">好评率</p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-600 mb-10 text-center">
                来看看漫迷们是如何评价非常爱漫画的
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    非常爱漫画真的太好用了！资源超全，更新也很快，最关键的是完全免费。下拉式阅读体验特别流畅，强烈推荐给每一个喜欢看漫画的朋友！
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-secondary-700">小</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">小漫迷阿星</p>
                      <p className="text-xs text-foreground-500">使用爱漫画 3 年</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    从大学开始就在非常爱漫画追漫画了，现在工作几年了还在看。界面简洁没广告，分类也很清晰，找漫画特别方便。希望爱漫画越做越好！
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-700">L</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">Leo追漫人</p>
                      <p className="text-xs text-foreground-500">使用爱漫画 5 年</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    朋友推荐的非常爱漫画，一用就爱上了。国漫日漫韩漫都有，分类很全。最喜欢下拉式阅读模式，一口气能看好多话，体验太棒了！
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent-200 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent-700">樱</span>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">樱花漫语</p>
                      <p className="text-xs text-foreground-500">使用爱漫画 1 年</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
                常见问题
              </h2>
              <p className="text-sm text-foreground-600 mb-10 text-center">
                解答您对非常爱漫画的疑问
              </p>
              <div className="max-w-3xl mx-auto space-y-3">
                {[
                  {
                    q: '非常爱漫画真的完全免费吗？',
                    a: '是的，非常爱漫画平台上所有漫画资源均免费提供阅读。我们致力于为漫迷打造无障碍的阅读体验，无需付费即可畅读全部内容。',
                  },
                  {
                    q: '漫画更新频率如何？',
                    a: '我们每天都会同步各大漫画平台的最新连载内容，确保您能第一时间追更喜欢的漫画。大部分热门漫画的更新延迟不超过24小时。',
                  },
                  {
                    q: '支持哪些设备阅读？',
                    a: '非常爱漫画完美适配电脑、平板和手机端，支持下拉式阅读模式。无论您使用什么设备，都能获得流畅舒适的阅读体验。',
                  },
                  {
                    q: '如何找到我想看的漫画？',
                    a: '您可以通过首页的分类导航按类型浏览，也可以使用搜索功能直接查找漫画名称。我们还设有热门推荐和最近更新栏目，帮助您发现更多精彩内容。',
                  },
                  {
                    q: '漫画画质如何？',
                    a: '非常爱漫画对漫画画质有严格标准，大部分漫画提供高清分辨率阅读，确保文字清晰、画面细腻，给您最佳的视觉享受。',
                  },
                ].map((faq, index) => (
                  <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-50/70 transition-colors">
                      <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-lg"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mb-10 text-center">
                如果您有任何问题或建议，欢迎随时联系我们
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto">
                <div className="space-y-5">
                  <div className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">contact@aimanhua.com</p>
                      <p className="text-xs text-foreground-500 mt-0.5">商务合作、版权问题请联系此邮箱</p>
                    </div>
                  </div>

                  <div className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                      <i className="ri-customer-service-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">在线客服</h3>
                      <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                      <p className="text-xs text-foreground-500 mt-0.5">点击右下角客服图标即可在线咨询</p>
                    </div>
                  </div>

                  <div className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/70">
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-600">
                      <i className="ri-links-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">社交媒体</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-wechat-line"></i>
                        </a>
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-weibo-line"></i>
                        </a>
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 hover:bg-primary-500 hover:text-white transition-colors cursor-pointer">
                          <i className="ri-bilibili-line"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-4">发送消息</h3>
                  {formStatus === 'success' && (
                    <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-200 text-sm text-primary-700">
                      消息已成功发送！我们会尽快回复您，感谢您的反馈。
                    </div>
                  )}
                  {formError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                      {formError}
                    </div>
                  )}
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的称呼
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的称呼"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                        placeholder="请输入您想说的内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}